import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-hot-toast'

import { ConfirmTransactionTemplate } from '@/components/ConfirmTransactionTemplate'
import { TransferInAvatar } from '@/components/TransferInAvatar'
import { useSavingsStore } from '@/store/useSavingsStore'
import { useUserStore } from '@/store/useUserStore'
import { useCreateGoalTransferIn } from '@/domain'
import type { WalletCurrency } from '@/domain'
import type { PaymentMethodsType } from '@/constants/paymentMethods'
import { currencyUtils } from '@/utils'

const { formatFiatCurrency, parseAmountToFloat, cryptoToBig } = currencyUtils

export default function ConfirmTransferScreen() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<
    PaymentMethodsType | undefined
  >(undefined)

  const goalTransferIn = useSavingsStore((s) => s.goalTransferIn)
  const completeCurrencies = useUserStore((s) => s.wallet?.completeCurrencies)

  // Find USDT in user wallet
  const usdtWallet = completeCurrencies?.find(
    (currency: WalletCurrency) => currency.code === 'USDT',
  )

  // Format amounts for display
  const formattedFiatAmount = formatFiatCurrency({
    value: Number(goalTransferIn.fiatAmount.amount),
    fiatCode: goalTransferIn.fiatAmount.code as any,
    showDecimals: true,
  })

  const parsedCryptoAmount = parseAmountToFloat({
    amount: goalTransferIn.cryptoAmount.amount,
    decimalSeparator: '.',
  })

  const bigCryptoAmount = cryptoToBig({
    amount: goalTransferIn.cryptoAmount.amount,
  })

  // Payment method change handler
  const handlePaymentMethodChange = (paymentMethod: PaymentMethodsType) => {
    setSelectedPaymentMethod(paymentMethod)
  }

  const redirectToTransferInSuccess = () => {
    navigate('/savings/transfer-in/success', {
      state: {
        amount: bigCryptoAmount,
        code: usdtWallet?.code || 'USDT',
        title: t('screens:transferIn.youDeposited'),
        description: t('screens:transferIn.toSavings'),
        relativeFiatFormatted: formattedFiatAmount,
        currencyLabel: usdtWallet?.name || 'USDT',
        doneRoute: `/savings/savings-detail/${goalTransferIn.saving_goal_id}`,
        navigationMethod: 'dismiss' as const,
      },
    })
  }

  const { mutateAsync: createTransferIn, isPending: isLoading } =
    useCreateGoalTransferIn({
      onSuccess: () => {
        redirectToTransferInSuccess()
      },
      onError: ({ errorMessage }) => {
        toast.error(errorMessage || t('api:errors.somethingWrong'))
      },
    })

  const handleConfirmTransfer = async () => {
    try {
      await createTransferIn({
        saving_goal_id: goalTransferIn.saving_goal_id,
        amount: parsedCryptoAmount,
        currency: goalTransferIn.cryptoAmount.code as any,
      })
    } catch (error) {
      console.error('Transfer failed:', error)
    }
  }

  return (
    <ConfirmTransactionTemplate
      screenTitle={t('form:actions.transferIn')}
      buttonTitle={t('form:actions.confirm', 'Confirm')}
      amountInCrypto={bigCryptoAmount}
      currency={usdtWallet}
      infoCurrency={formattedFiatAmount}
      onSubmit={handleConfirmTransfer}
      isLoadingSubmit={isLoading}
      byScreen="transferIn"
      estimatedEarnings="5.5" // TODO: Fetch from API
      annualReturn={(parsedCryptoAmount * 0.055).toFixed(6)} // Return in USDT format
      AvatarCurrencyCustom={<TransferInAvatar />}
      _shouldShowWallet={true}
      _shouldShowOnRampMethods={false}
      // Payment Method props
      selectedPaymentMethod={selectedPaymentMethod}
      onSelectPaymentMethod={handlePaymentMethodChange}
    />
  )
}
